import { settings, type Action } from '../../app/settings';
import { Emitter } from '../../app/store';

export interface InputEvents extends Record<string, unknown> {
  press: Action;
  emote: number;
  escape: null;
  wheel: number;
}

/**
 * Entrada unificada: teclado (con teclas remapeables), ratón con pointer lock y controles táctiles.
 * Expone ejes de movimiento y acumula el giro de cámara para que lo consuma la cámara.
 */
export class Input extends Emitter<InputEvents> {
  private down = new Set<string>();
  lookDX = 0;
  lookDY = 0;
  enabled = true;
  /** Joystick táctil (−1..1). */
  touchMove = { x: 0, y: 0 };
  touchSprint = false;
  private jumpQueued = false;
  readonly isTouch = matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;

  constructor(private canvas: HTMLElement) {
    super();
    window.addEventListener('keydown', (e) => this.onKey(e, true));
    window.addEventListener('keyup', (e) => this.onKey(e, false));
    window.addEventListener('blur', () => this.down.clear());
    canvas.addEventListener('click', () => {
      if (this.enabled && !this.isTouch && document.pointerLockElement !== canvas) canvas.requestPointerLock?.();
    });
    document.addEventListener('mousemove', (e) => {
      if (document.pointerLockElement === canvas && this.enabled) {
        this.lookDX += e.movementX;
        this.lookDY += e.movementY;
      }
    });
    canvas.addEventListener('wheel', (e) => this.emit('wheel', Math.sign(e.deltaY)), { passive: true });
  }

  get locked() {
    return document.pointerLockElement === this.canvas;
  }

  unlock() {
    if (document.pointerLockElement) document.exitPointerLock();
  }

  private typing(e: KeyboardEvent) {
    const t = e.target as HTMLElement;
    return t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
  }

  private onKey(e: KeyboardEvent, isDown: boolean) {
    if (this.typing(e)) return;
    if (e.code === 'Escape' && isDown) {
      this.emit('escape', null);
      return;
    }
    const b = settings.get().bindings;
    const action = (Object.keys(b) as Action[]).find((a) => b[a] === e.code);
    if (action === 'scoreboard' || action === 'jump') e.preventDefault();
    if (isDown) {
      if (!this.down.has(e.code)) {
        if (action) {
          if (action === 'jump') this.jumpQueued = true;
          if (this.enabled || action === 'chat' || action === 'scoreboard') this.emit('press', action);
        }
        if (/^Digit[1-6]$/.test(e.code) && this.enabled) this.emit('emote', Number(e.code.slice(5)));
      }
      this.down.add(e.code);
    } else this.down.delete(e.code);
  }

  isDown(a: Action) {
    return this.enabled && this.down.has(settings.get().bindings[a]);
  }

  /** Ejes de movimiento en el marco de la cámara. */
  axes(): { mx: number; mz: number; sprint: boolean } {
    if (!this.enabled) return { mx: 0, mz: 0, sprint: false };
    let mx = (this.isDown('right') ? 1 : 0) - (this.isDown('left') ? 1 : 0) + this.touchMove.x;
    let mz = (this.isDown('forward') ? 1 : 0) - (this.isDown('back') ? 1 : 0) + this.touchMove.y;
    const len = Math.hypot(mx, mz);
    if (len > 1) {
      mx /= len;
      mz /= len;
    }
    return { mx, mz, sprint: this.isDown('sprint') || this.touchSprint || Math.hypot(this.touchMove.x, this.touchMove.y) > 0.92 };
  }

  /** Salto «bufferizado»: se consume en el siguiente paso fijo. */
  consumeJump(): boolean {
    const j = this.jumpQueued && this.enabled;
    this.jumpQueued = false;
    return j;
  }

  queueJump() {
    this.jumpQueued = true;
  }

  takeLook(): [number, number] {
    const r: [number, number] = [this.lookDX, this.lookDY];
    this.lookDX = this.lookDY = 0;
    return r;
  }
}
